import { Add, GetApp, FilterList, Close, Search, ExpandMore, Refresh } from "@mui/icons-material";
import { Box, Button, Card, styled, useTheme, Grid, Tabs, Tab, Badge, alpha, useMediaQuery, TextField, InputAdornment, IconButton, Collapse, FormControl, InputLabel, Select, MenuItem, Chip, Pagination } from "@mui/material";
import LocationCityIcon from '@mui/icons-material/LocationCity';
import Public from '@mui/icons-material/Public';
import LibraryBooks from '@mui/icons-material/LibraryBooks';
import CurrencyRupee from '@mui/icons-material/CurrencyRupee';
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { H6 } from "components/Typography";
import DataTable from "page-sections/data-table/dataTableV2/DataTable";
import LeadCard from "page-sections/dashboards/CRM/LeadCard";
import LongCard from "page-sections/dashboards/CRM/LongCard";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import AddDistrictModal from "page-sections/data-table/dataTableV2/AddDistrictModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { usePageTitle } from "contexts/PageTitleContext";
import { Typography } from "@mui/material";
import ProductList from "./productList";
import AdvancedExportModal from "./AdvancedExportModal";
// import { usePageTitle } from "contexts/PageTitleContext";
// Styled Components for Filters
const FilterContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  overflow: 'visible',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: alpha(theme.palette.primary.main, 0.02),
  borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
}));

const FilterContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  position: 'relative',
  zIndex: 1000, // High z-index to ensure date pickers appear above everything
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  '& .MuiTextField-root': {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      },
      '&.Mui-focused': {
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
    },
  },
}));

const DatePickerField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    '&.Mui-focused': {
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
  },
  '& .MuiInputBase-input': {
    cursor: 'pointer',
    colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light',
  },
  '& input[type="date"]': {
    colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light',
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'invert(0)',
    cursor: 'pointer',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  boxShadow: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
}));


const StatusChip = styled(Card)(({ theme, statuscolor }) => ({
  fontWeight: 600,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: statuscolor ? alpha(statuscolor, 0.1) : alpha(theme.palette.grey[500], 0.1),
  color: statuscolor || theme.palette.grey[700],
  border: `1px solid ${statuscolor ? alpha(statuscolor, 0.2) : alpha(theme.palette.grey[500], 0.2)}`,
  fontSize: '0.75rem',
}));

// Export utility functions
const exportToCSV = (data, filename = 'products_export.csv') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  // Define headers for CSV
  const headers = [
    'Order ID',
    'User Name',
    'User Email',
    'Mobile Number',
    'Status',
    'Payment Status',
    'Payment Method',
    'Total Amount',
    'Order Date',
    'Delivery Address',
    'Created At'
  ];

  // Convert data to CSV format
  const csvContent = [
    headers.join(','), // Header row
    ...data.map(product => [
      `"${product.order_id || ''}"`,
      `"${product.user?.username || product.user_name || ''}"`,
      `"${product.user?.email || product.user_email || ''}"`,
      `"${product.user?.mobile_number || product.mobile_number || ''}"`,
      `"${product.status?.name || product.order_status_display || ''}"`,
      `"${product.payment_status?.name || product.payment_status_display || ''}"`,
      `"${product.payment_method?.method || product.payment_method_name || ''}"`,
      `"${product.total_amount || ''}"`,
      `"${product.order_date_formatted || ''}"`,
      `"${product.delivery_address || (product.address_details ? `${product.address_details.full_address}, ${product.address_details.village}` : '')}"`,
      `"${product.created_at ? new Date(product.created_at).toLocaleDateString() : ''}"`
    ].join(','))
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToExcel = async (data, filename = 'products_export.xlsx') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  try {
    // Dynamic import for xlsx library
    const XLSX = await import('xlsx');
    
    // Prepare data for Excel
    const excelData = data.map(product => ({
      'Order ID': product.order_id || '',
      'User Name': product.user?.username || product.user_name || '',
      'User Email': product.user?.email || product.user_email || '',
      'Mobile Number': product.user?.mobile_number || product.mobile_number || '',
      'Status': product.status?.name || product.order_status_display || '',
      'Payment Status': product.payment_status?.name || product.payment_status_display || '',
      'Payment Method': product.payment_method?.method || product.payment_method_name || '',
      'Total Amount': product.total_amount || 0,
      'Order Date': product.order_date_formatted || '',
      'Delivery Address': product.delivery_address || (product.address_details ? `${product.address_details.full_address}, ${product.address_details.village}` : ''),
      'Created At': product.created_at ? new Date(product.created_at).toLocaleDateString('en-IN') : ''
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const colWidths = Object.keys(excelData[0] || {}).map((key, index) => {
      let maxWidth = key.length;
      excelData.forEach(row => {
        const cellValue = String(row[key] || '');
        maxWidth = Math.max(maxWidth, cellValue.length);
      });
      const width = Math.min(Math.max(maxWidth + 2, 12), 50);
      return { wch: width };
    });
    
    worksheet['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Products Export');

    // Save file with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const finalFilename = filename.replace('.xlsx', `_${timestamp}.xlsx`);
    
    XLSX.writeFile(workbook, finalFilename);
    
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    alert('Error exporting to Excel. Please try again or use CSV export instead.');
  }
};

const ButtonWrapper = styled(FlexBox)(({
  theme
}) => ({
  [theme.breakpoints.down(500)]: {
    marginTop: 10,
    width: "100%",
    flexDirection: "column-reverse",
    "& > .MuiBox-root": {
      width: "100%",
      margin: "10px 0",
      alignItems: "center",
      flexDirection: "column"
    },
    "& .MuiButton-root": {
      minWidth: "100%"
    }
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  minHeight: 48,
  width: '100%',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-flexContainer': {
    gap: 4,
    padding: 4,
  },
  '& .MuiTabs-scroller': {
    [theme.breakpoints.up('md')]: {
      '& .MuiTabs-flexContainer': {
        justifyContent: 'space-between',
      },
    },
  },
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 2px 8px rgba(0,0,0,0.3)' 
    : '0 2px 8px rgba(0,0,0,0.1)',
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  minHeight: 40,
  borderRadius: theme.shape.borderRadius,
  margin: 0,
  padding: '8px 16px',
  color: theme.palette.text.secondary,
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  
  // Desktop: Equal width distribution
  [theme.breakpoints.up('md')]: {
    flex: 1,
    minWidth: 0,
  },
  
  // Mobile: Minimum width for scrolling
  [theme.breakpoints.down('md')]: {
    minWidth: 120,
    padding: '8px 12px',
    fontSize: '0.8rem',
  },
  
  [theme.breakpoints.down('sm')]: {
    minWidth: 100,
    padding: '6px 8px',
    fontSize: '0.75rem',
  },

  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const getStatusColor = (theme, status) => {
  const colors = {
    all: theme.palette.text.primary,
    pending: theme.palette.warning.main,
    confirmed: theme.palette.info.main,
    processing: theme.palette.primary.main,
    shipped: theme.palette.secondary.main,
    delivered: theme.palette.success.main,
    cancelled: theme.palette.error.main,
    returned: theme.palette.error.dark,
  };
  return colors[status] || theme.palette.grey[500];
};

const TabContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: theme.palette.background.default,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

const Products = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { setPageTitle } = usePageTitle();

  // Check if user is Company type
  const isCompanyUser = apiService.isCompanyUser();
  const companyId = apiService.getCompanyId();
  const [districts, setDistricts] = useState([]);
  const [summaryTotals, setSummaryTotals] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(1)));
  const [endDate, setEndDate] = useState(new Date());

  const [products, setProducts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [statuses, setStatuses] = useState([]);
  // Pagination state coming from backend
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];
  const [showFilters, setShowFilters] = useState(false); // Collapsed by default
  const [isResetClicked, setIsResetClicked] = useState(false); // Track if reset was clicked
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(isCompanyUser ? companyId : 'all');
  
  // Selection states
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  
  // Modal states
  const [advancedExportModal, setAdvancedExportModal] = useState(false);
  
  // Ref to prevent duplicate API calls
  const isFetchingStatuses = useRef(false);

  const getStatusIcon = (category) => {
    const key = (category || '').toLowerCase();
    switch (key) {
      case 'pending': return '⏳';
      case 'confirmed': return '✅';
      case 'processing': return '⚙️';
      case 'shipped': return '🚚';
      case 'delivered': return '📦';
      case 'cancelled': return '❌';
      case 'returned': return '↩️';
      case 'all': return '📊';
      default: return '🏷️';
    }
  };

  useEffect(() => {
    setPageTitle('App Order');
  }, [setPageTitle]);

  // Fetch companies
  const fetchCompanies = async () => {
    try {
      // If Company user, create a company array with their company details
      if (isCompanyUser && companyId) {
        const companyDetails = apiService.getCompanyDetails();
        if (companyDetails) {
          setCompanies([companyDetails]);
        }
        return;
      }
      
      const res = await apiService.get(API_ENDPOINTS.GET_company);
      if (res?.success) {
        const companyList = res.data?.Company || [];
        setCompanies(companyList);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Format date for API - returns null if empty to indicate no date filter
  const formatDateForAPI = (date) => {
    if (!date || date === '') {
      return null; // Return null to indicate no date filter
    }
    if (typeof date === 'string') {
      return date;
    }
    return date.toISOString().split('T')[0];
  };

  // Fetch dynamic statuses with counts from API
  const fetchStatuses = async (preserveCurrentFilter = false, skipProductFetch = false, companyOverride = null) => {
    // Prevent duplicate calls
    if (isFetchingStatuses.current) {
      return;
    }
    
    isFetchingStatuses.current = true;
    try {
      const userId = localStorage.getItem("user_id");
      const fromDate = formatDateForAPI(startDate);
      const toDate = formatDateForAPI(endDate);

      let url = `${API_ENDPOINTS.GET_order_status_choices_with_count}?user_id=${userId}&type=normal`;
      
      // Add date filters only if dates are provided
      if (fromDate) {
        url += `&from_date=${fromDate}`;
      }
      if (toDate) {
        url += `&to_date=${toDate}`;
      }
      
      // Add company filter (use override if provided)
      let companyToUse;
      if (isCompanyUser && companyId) {
        companyToUse = companyId;
      } else {
        companyToUse = companyOverride ?? selectedCompany;
      }
      if (companyToUse && companyToUse !== 'all') {
        url += `&company=${companyToUse}`;
      }

      const response = await apiService.get(url);

      if (response?.success) {
        // Handle different possible response formats
        const apiStatuses = Array.isArray(response.data?.statuses) 
          ? response.data.statuses 
          : Array.isArray(response.data) 
            ? response.data 
            : [];
        const sorted = [...apiStatuses].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        setStatuses(sorted);

        if (!skipProductFetch) {
          if (preserveCurrentFilter && statusFilter) {
            // Preserve current status filter and refresh products with it
            fetchProducts(statusFilter, companyOverride);
          } else {
            // Set to first status (initial load)
            const initial = sorted[0]?.name || '';
            setStatusFilter(initial);
            fetchProducts(initial, companyOverride);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching statuses:", error);
    } finally {
      isFetchingStatuses.current = false;
    }
  };

  const fetchProducts = async (status = statusFilter, companyOverride = null, page = currentPage, pageSizeParam = pageSize) => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem("user_id");
      const fromDate = formatDateForAPI(startDate);
      const toDate = formatDateForAPI(endDate);
      const statusParam = !status ? '' : `&status=${encodeURIComponent(status)}`;
      
      let url = `${API_ENDPOINTS.GET_products}?user_id=${userId}${statusParam}&type=normal`;
      
      // Add date filters only if dates are provided
      if (fromDate) {
        url += `&from_date=${fromDate}`;
      }
      if (toDate) {
        url += `&to_date=${toDate}`;
      }
      
      // Add company filter (use override if provided)
      let companyToUse;
      if (isCompanyUser && companyId) {
        companyToUse = companyId;
      } else {
        companyToUse = companyOverride ?? selectedCompany;
      }
      if (companyToUse && companyToUse !== 'all') {
        url += `&company=${companyToUse}`;
      }
      // Add pagination params
      if (page) url += `&page=${page}`;
      if (pageSizeParam) url += `&page_size=${pageSizeParam}`;
      
      const response = await apiService.get(url);
      
      if (response) {
        const list = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.results)
            ? response.results
            : Array.isArray(response)
              ? response
              : [];
        setProducts(list);

        // Try to extract pagination info from common response shapes
        const paginationData = response.pagination || response?.meta || response?.data?.pagination || response?.pagination || {};
        setPagination(prev => ({
          current_page: paginationData.current_page ?? page,
          page_size: paginationData.page_size ?? pageSizeParam,
          total_items: paginationData.total_items ?? (Array.isArray(list) ? list.length : 0),
          total_pages: paginationData.total_pages ?? 1,
          has_next: paginationData.has_next ?? false,
          has_previous: paginationData.has_previous ?? false
        }));
        setCurrentPage(paginationData.current_page ?? page);
        setPageSize(paginationData.page_size ?? pageSizeParam);

        // Fetch statuses after products are loaded to update counts (skip product fetch to avoid loop)
        const companyForStatusUpdate = companyOverride ?? selectedCompany;
        fetchStatuses(true, true, companyForStatusUpdate); // Preserve current filter and skip product fetch
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Initial load with default date range and company filter
    fetchStatuses(false, false, selectedCompany);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set default dates when filter is opened and dates are empty (but not after reset)
  useEffect(() => {
    if (showFilters && (!startDate || startDate === '') && (!endDate || endDate === '') && !isResetClicked) {
      setStartDate(new Date(new Date().setDate(1)));
      setEndDate(new Date());
    }
    // Reset the flag when filter is closed
    if (!showFilters) {
      setIsResetClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilters]);

  // Removed automatic API calls on date/company changes - API will only be called on "Apply Filter" click

  const handleStatusChange = (event, newValue) => {
    setStatusFilter(newValue);
    // Reset to first page when status changes
    fetchProducts(newValue, selectedCompany, 1, pageSize);
  };

  const handleRefresh = async () => {
    // Fetch statuses to update counts, but maintain current status filter and company
    await fetchStatuses(true, false, selectedCompany); // Pass true to preserve current filter and company
    // refresh products keeping current page and page size
    fetchProducts(statusFilter, selectedCompany, currentPage, pageSize);
  };

  const handleApplyDateFilter = () => {
    // Fetch statuses first, which will then fetch products with the current status filter and company
    fetchStatuses(true, false, selectedCompany); // Preserve current filter and company, fetch products
    // ensure products are fetched for first page with current page size
    fetchProducts(statusFilter, selectedCompany, 1, pageSize);
  };

  const handleCompanyChange = (companyValue) => {
    setSelectedCompany(companyValue);
    // Immediately fetch statuses and products for the new company selection
    fetchStatuses(true, false, companyValue);
    // reset to page 1 for new company
    fetchProducts(statusFilter, companyValue, 1, pageSize);
  }; 

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchProducts(statusFilter, selectedCompany, value, pageSize);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    // go back to first page when page size changes
    setCurrentPage(1);
    fetchProducts(statusFilter, selectedCompany, 1, newSize);
  };

  // Reset selections when status filter changes
  useEffect(() => {
    setSelectedProducts([]);
    setIsAllSelected(false);
  }, [statusFilter]);

  // Reset selections when company filter changes
  useEffect(() => {
    setSelectedProducts([]);
    setIsAllSelected(false);
  }, [selectedCompany]);

  // Selection handlers
  const handleProductSelect = (productId, isSelected) => {
    setSelectedProducts(prev => {
      if (isSelected) {
        const newSelected = [...prev, productId];
        setIsAllSelected(newSelected.length === products.length);
        return newSelected;
      } else {
        const newSelected = prev.filter(id => id !== productId);
        setIsAllSelected(false);
        return newSelected;
      }
    });
  };

  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      const allProductIds = products.map(product => product.id);
      setSelectedProducts(allProductIds);
      setIsAllSelected(true);
    } else {
      setSelectedProducts([]);
      setIsAllSelected(false);
    }
  };

  // Export handlers
  const handleExport = (format) => {
    const selectedData = products.filter(product => 
      selectedProducts.includes(product.id)
    );

    if (selectedData.length === 0) {
      alert('Please select products to export');
      return;
    }

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `products_export_${timestamp}`;

    if (format === 'excel') {
      exportToExcel(selectedData, `${filename}.xlsx`);
    } else if (format === 'csv') {
      exportToCSV(selectedData, `${filename}.csv`);
    }
  };

  // Download invoices and/or delivery challans as a ZIP
  const handleDownloadZip = async (mode) => {
    try {
      const selectedData = products.filter(product => selectedProducts.includes(product.id));
      if (selectedData.length === 0) {
        alert('Please select orders first');
        return;
      }

      const orderIds = selectedData
        .map(product => product.order_id)
        .filter(Boolean);

      if (orderIds.length === 0) {
        alert('Selected orders do not have valid order IDs');
        return;
      }

      const query = new URLSearchParams({
        order_ids: orderIds.join(','),
        type: mode
      }).toString();

      const filename = `orders_${mode}_${new Date().toISOString().slice(0, 10)}.zip`;

      await apiService.downloadFile(`${API_ENDPOINTS.GET_generate_zip_file}?${query}`, filename);
    } catch (error) {
      console.error('Error creating ZIP:', error);
      alert(error?.message || 'Failed to download documents ZIP. Please try again.');
    }
  };

  return (
    <Box pt={2} pb={4}>
      {/* Advanced Filters */}
      <FilterContainer>
        <FilterHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <FilterList color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Filters & Search
              </Typography>
              {/* Check if any filters are active - Always show date range when set */}
              {(() => {
                const getDateStr = (date, isStart) => {
                  if (!date || date === '') return '';
                  if (date instanceof Date) return date.toISOString().split('T')[0];
                  if (typeof date === 'string') return date;
                  return isStart ? new Date(new Date().setDate(1)).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
                };
                const startDateStr = getDateStr(startDate, true);
                const endDateStr = getDateStr(endDate, false);
                // Always show active indicator when date range is set (even if default)
                const hasDateRange = startDateStr && endDateStr;
                const hasActiveFilters = searchValue || hasDateRange;
                
                return hasActiveFilters && (
                  <>
                    <StatusChip
                      statuscolor={theme.palette.success.main}
                      sx={{ ml: 1 }}
                    >
                      Active
                    </StatusChip>
                    {hasDateRange && (
                      <StatusChip
                        statuscolor={theme.palette.info.main}
                        sx={{ ml: 0.5 }}
                      >
                        📅 {new Date(startDateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(endDateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </StatusChip>
                    )}
                  </>
                );
              })()}
            </Box>
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            >
              <ExpandMore />
            </IconButton>
          </Box>
        </FilterHeader>

        <Collapse in={showFilters}>
          <FilterContent>
            <Grid container spacing={3}>
              {/* Date Range */}
              <Grid item xs={12} md={12}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Date Range Filter
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DatePickerField
                      fullWidth
                      label="From Date"
                      type="date"
                      value={startDate instanceof Date ? startDate.toISOString().split('T')[0] : (typeof startDate === 'string' && startDate !== '' ? startDate : '')}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setStartDate(newDate);
                        setIsResetClicked(false); // Clear reset flag when user manually changes date
                      }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        max: endDate instanceof Date ? endDate.toISOString().split('T')[0] : (typeof endDate === 'string' && endDate !== '' ? endDate : new Date().toISOString().split('T')[0]),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePickerField
                      fullWidth
                      label="To Date"
                      type="date"
                      value={endDate instanceof Date ? endDate.toISOString().split('T')[0] : (typeof endDate === 'string' && endDate !== '' ? endDate : '')}
                      onChange={(e) => {
                        const newDate = e.target.value;
                        setEndDate(newDate);
                        setIsResetClicked(false); // Clear reset flag when user manually changes date
                      }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: startDate instanceof Date ? startDate.toISOString().split('T')[0] : (typeof startDate === 'string' && startDate !== '' ? startDate : new Date(new Date().setDate(1)).toISOString().split('T')[0]),
                        max: new Date().toISOString().split('T')[0],
                      }}
                    />
                  </Grid>
                </Grid>
                {/* Date Range Display Chip */}
                {(() => {
                  const getDisplayDate = (date, isStart) => {
                    if (!date || date === '') {
                      return isStart ? new Date(new Date().setDate(1)) : new Date();
                    }
                    if (date instanceof Date) return date;
                    if (typeof date === 'string') return new Date(date);
                    return isStart ? new Date(new Date().setDate(1)) : new Date();
                  };
                  const displayStartDate = getDisplayDate(startDate, true);
                  const displayEndDate = getDisplayDate(endDate, false);
                  return (
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <StatusChip
                        statuscolor={theme.palette.info.main}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.info.main, 0.2),
                            transform: 'scale(1.02)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        📅 Date Range: {displayStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} to {displayEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </StatusChip>
                    </Box>
                  );
                })()}
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
                  <ActionButton
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => {
                      setSearchValue('');
                      setStartDate('');
                      setEndDate('');
                      setIsResetClicked(true); // Mark that reset was clicked
                      if (!isCompanyUser) {
                        setSelectedCompany('all');
                        // Apply reset immediately for non-company users
                        fetchStatuses(false, false, 'all');
                      } else {
                        fetchStatuses();
                      }
                    }}
                    sx={{
                      borderColor: theme.palette.error.main,
                      color: theme.palette.error.main,
                      '&:hover': {
                        borderColor: theme.palette.error.dark,
                        backgroundColor: alpha(theme.palette.error.main, 0.05)
                      }
                    }}
                  >
                    Reset Filters
                  </ActionButton>
                  <ActionButton
                    variant="contained"
                    startIcon={<FilterList />}
                    onClick={handleApplyDateFilter}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      }
                    }}
                  >
                    Apply Filters
                  </ActionButton>
                </Box>
              </Grid>
            </Grid>
          </FilterContent>
        </Collapse>
      </FilterContainer>

      {/* Status Tabs */}
      <TabContainer>
        <StyledTabs
          value={statusFilter}
          onChange={handleStatusChange}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
        >
          {statuses.map((s) => (
            <StyledTab
              key={s.id}
              value={s.name}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{getStatusIcon(s.category || s.name)}</span>
                    <span>{s.name}</span>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: theme => getStatusColor(theme, s.category || s.name),
                      lineHeight: 1,
                    }}
                  >
                    {s.count || 0}
                  </Typography>
                </Box>
              }
            />
          ))}
        </StyledTabs>
      </TabContainer>

      {/* Search Controls & Action Buttons */}
      <FlexBetween flexWrap="wrap" mb={2}>
        <FlexBox alignItems="center" gap={3}>
          <SearchInput 
            bordered={false} 
            placeholder="Search by Order ID or Email" 
            onChange={e => setSearchValue(e.target.value)} 
            value={searchValue}
              sx={{ 
                  minWidth: 250, 
                  width: { xs: "100%", sm: 300, md: 400 } // grows with screen size
                }}
          />
        </FlexBox>
        
        <FlexBox alignItems="center" gap={2}>
          {/* Selection Info */}
          {selectedProducts.length > 0 && (
            <Typography variant="body2" color="primary" fontWeight={600}>
              {selectedProducts.length} selected
            </Typography>
          )}


           {/* Company Filter */}
              {!isCompanyUser && (
                <Grid item xs={12} md={6}>
                  {/* <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Category
                  </Typography> */}
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select 
                      value={selectedCompany} 
                      label="Category" 
                      onChange={(e) => {
                        handleCompanyChange(e.target.value);
                      }}
                    >
                      <MenuItem value="all">
                        <Chip label="All Companies" size="small" color="default" sx={{ mr: 0 }} />
                        All Category
                      </MenuItem>
                      {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          <Chip label={company.name} size="small" color="secondary" variant="outlined" sx={{ mr: 1 }} />
                          {company.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
          

          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={() => setAdvancedExportModal(true)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 140,
            }}
          >
            Advanced Export
          </Button>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Rows</InputLabel>
              <Select
                value={pageSize}
                label="Rows"
                onChange={handlePageSizeChange}
              >
                {pageSizeOptions.map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* <Typography variant="body2" color="text.secondary">
              {pagination?.total_items > 0
                ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items} orders`
                : `Showing ${products.length} ${statusFilter ? `${statusFilter} orders` : 'orders'}`}
            </Typography>

            {pagination?.total_pages > 1 && (
              <Pagination count={pagination.total_pages} page={currentPage} onChange={handlePageChange} color="primary" />
            )} */}
          </Box>
        </FlexBox>
      </FlexBetween>

      {/* Orders Table */}
      <Card sx={{ marginTop: 2 }}>
        <ProductList
          data={products} 
          searchQuery={searchValue} 
          statusFilter={statusFilter}
          isLoading={isLoading} 
          onRefresh={handleRefresh}
          selectedProducts={selectedProducts}
          onProductSelect={handleProductSelect}
          onSelectAll={handleSelectAll}
          isAllSelected={isAllSelected}
          onExport={handleExport}
          onZipDownload={handleDownloadZip}
        />
      </Card>

        {/* Showing range and pagination below the table */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                            
                              {/* <Typography variant="body2" color="text.secondary">
                    {pagination?.total_items > 0
                      ? `Showing.${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items} orders`
                      : `Showing ${products.length} ${statusFilter ? `${statusFilter} orders` : 'orders'}`}
                  </Typography> */}
      
                  {pagination?.total_pages > 1 && (
                    <Pagination count={pagination.total_pages} page={currentPage} onChange={handlePageChange} color="primary" />
                  )}
                   {/* <Typography variant="body2" color="text.secondary">
                    {pagination?.total_items > 0
                      ? `Showing.${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items} orders`
                      : `Showing ${products.length} ${statusFilter ? `${statusFilter} orders` : 'orders'}`}
                  </Typography>
                            */}
      
                            {/* {(pagination?.total_pages ?? 1) > 1 && (
                              <Pagination
                                count={pagination.total_pages ?? 1}
                                page={currentPage ?? (pagination?.current_page ?? 1)}
                                onChange={(e, value) => { if (typeof onPageChange === 'function') onPageChange(e, value); }}
                                color="primary"
                              />
                            )} */}
                          </Box>
                          <Box
  sx={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    mt: 3,
  }}
>
  <Typography variant="body2" color="text.secondary">
    {pagination.total_items > 0
      ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(
          pagination.current_page * pagination.page_size,
          pagination.total_items
        )} of ${pagination.total_items}`
      : "No courses"}
  </Typography>
</Box>
      

      {/* Advanced Export Modal */}
      <AdvancedExportModal
        open={advancedExportModal}
        onClose={() => setAdvancedExportModal(false)}
        onRefresh={handleRefresh}
      />
    </Box>
  );
};

export default Products;